// @ts-nocheck
import { useState, useEffect, useRef, useCallback } from 'react'
import { ISRAELI_CITIES, searchCities, IsraeliCity, REGION_LABELS, CITIES_BY_REGION } from './israeliCities'

interface IsraelCitySelectorProps {
  value?: { lat: number; lng: number; city?: string }
  onChange: (location: { lat: number; lng: number; city: string }) => void
  placeholder?: string
}

// Search Photon API (OSM-based) for cities/towns worldwide — supports autocomplete/partial matching
async function searchGeo(query: string): Promise<IsraeliCity[]> {
  try {
    const params = new URLSearchParams({
      q: query,
      limit: '8',
      lang: 'default',
    })
    const response = await fetch(`https://photon.komoot.io/api/?${params}`, {
      headers: { 'User-Agent': 'FBZipper/1.0' }
    })
    if (!response.ok) return []
    const data = await response.json()
    return (data.features || [])
      .filter((f: any) => ['city', 'town', 'village', 'hamlet', 'locality', 'district'].includes(f.properties?.type))
      .map((f: any) => {
        const p = f.properties
        const displayName = p.name || query
        const country = p.country || ''
        const state = p.state || p.county || ''
        return {
          name: [displayName, state, country].filter(Boolean).join(', '),
          nameHe: displayName,
          lat: f.geometry.coordinates[1],
          lng: f.geometry.coordinates[0],
          region: 'nominatim' as string,
        }
      })
  } catch {
    return []
  }
}

export function IsraelCitySelector({ value, onChange, placeholder = 'חפש עיר...' }: IsraelCitySelectorProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<IsraeliCity[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [showMap, setShowMap] = useState(false)
  const [selectedCity, setSelectedCity] = useState<IsraeliCity | null>(null)
  const [searching, setSearching] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const nominatimTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Initialize selected city from value - react to value changes
  useEffect(() => {
    if (value?.city) {
      const city = ISRAELI_CITIES.find(c => c.nameHe === value.city || c.name === value.city)
      if (city) {
        setSelectedCity(city)
        setSearchQuery(city.nameHe)
      } else if (value.lat && value.lng) {
        // City from API, not in local list
        setSelectedCity({ name: value.city, nameHe: value.city, lat: value.lat, lng: value.lng })
        setSearchQuery(value.city)
      }
    } else if (!value) {
      setSelectedCity(null)
      setSearchQuery('')
    }
  }, [value?.city])

  // Search when query changes - local first, then API
  useEffect(() => {
    let cancelled = false

    if (searchQuery.length >= 2) {
      const localResults = searchCities(searchQuery)
      setSearchResults(localResults)
      setShowDropdown(true)

      // Debounced API search if local results are few
      if (nominatimTimeout.current) clearTimeout(nominatimTimeout.current)
      if (localResults.length < 3) {
        setSearching(true)
        nominatimTimeout.current = setTimeout(async () => {
          const apiResults = await searchGeo(searchQuery)
          if (cancelled) return
          // Deduplicate: use fresh local results, exclude matches by nameHe
          const freshLocal = searchCities(searchQuery)
          const allNames = new Set(freshLocal.map(c => c.nameHe))
          const unique: IsraeliCity[] = []
          for (const c of apiResults) {
            if (!allNames.has(c.nameHe)) {
              allNames.add(c.nameHe)
              unique.push(c)
            }
          }
          setSearchResults([...freshLocal, ...unique])
          setShowDropdown(true)
          setSearching(false)
        }, 400)
      } else {
        setSearching(false)
      }
    } else {
      setSearchResults([])
      setShowDropdown(false)
      setSearching(false)
    }
    return () => {
      cancelled = true
      if (nominatimTimeout.current) clearTimeout(nominatimTimeout.current)
    }
  }, [searchQuery])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectCity = (city: IsraeliCity) => {
    setSelectedCity(city)
    setSearchQuery(city.nameHe)
    setShowDropdown(false)
    setShowMap(false)
    onChange({ lat: city.lat, lng: city.lng, city: city.nameHe })
  }

  return (
    <div className="israel-city-selector" style={{ position: 'relative' }}>
      {/* Search Input */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ position: 'relative', flex: 1 }} ref={dropdownRef}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (searchResults.length > 0) setShowDropdown(true)
            }}
            placeholder={placeholder}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e4e6ef',
              borderRadius: '8px',
              fontSize: '14px',
              direction: 'rtl'
            }}
          />

          {/* Search Results Dropdown */}
          {showDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              left: 0,
              maxHeight: '250px',
              overflowY: 'auto',
              background: 'white',
              border: '1px solid #e4e6ef',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              marginTop: '4px'
            }}>
              {searchResults.map((city, idx) => (
                <div
                  key={`${city.name}-${city.lat}-${city.lng}`}
                  onClick={() => handleSelectCity(city)}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f1f1f1',
                    direction: 'rtl',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <div style={{ fontWeight: 500 }}>
                    {city.nameHe}
                    {city.region === 'nominatim' && (
                      <span style={{ fontSize: '11px', color: '#1877f2', marginRight: '6px' }}>
                        <i className="bi bi-globe2" style={{ marginLeft: '2px' }}></i>
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#a1a5b7' }}>{city.name}</div>
                </div>
              ))}
              {searching && (
                <div style={{ padding: '10px 12px', textAlign: 'center', color: '#a1a5b7', fontSize: '13px' }}>
                  <span className="spinner-border spinner-border-sm me-2" style={{ width: '14px', height: '14px' }}></span>
                  מחפש...
                </div>
              )}
              {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                <div style={{ padding: '10px 12px', textAlign: 'center', color: '#a1a5b7', fontSize: '13px', direction: 'rtl' }}>
                  לא נמצאו תוצאות
                </div>
              )}
            </div>
          )}
        </div>

        {/* Map Toggle Button */}
        <button
          type="button"
          onClick={() => setShowMap(!showMap)}
          style={{
            padding: '10px 16px',
            background: showMap ? '#1877f2' : '#f1f1f2',
            color: showMap ? 'white' : '#5e6278',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          <i className="bi bi-map"></i>
          מפה
        </button>
      </div>

      {/* Selected City Display */}
      {selectedCity && (
        <div style={{
          marginTop: '8px',
          padding: '8px 12px',
          background: '#e8f4fd',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          direction: 'rtl'
        }}>
          <div>
            <i className="bi bi-geo-alt-fill" style={{ color: '#1877f2', marginLeft: '6px' }}></i>
            <span style={{ fontWeight: 500 }}>{selectedCity.nameHe}</span>
            <span style={{ color: '#6c757d', fontSize: '12px', marginRight: '8px' }}>
              ({selectedCity.lat.toFixed(4)}, {selectedCity.lng.toFixed(4)})
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setSelectedCity(null)
              setSearchQuery('')
              onChange({ lat: 0, lng: 0, city: '' })
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#dc3545',
              padding: '4px'
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {/* Interactive Map */}
      {showMap && (
        <div style={{
          marginTop: '12px',
          border: '1px solid #e4e6ef',
          borderRadius: '12px',
          overflow: 'hidden',
          background: '#f8f9fa'
        }}>
          {/* Map Header */}
          <div style={{
            padding: '12px 16px',
            background: '#fff',
            borderBottom: '1px solid #e4e6ef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontWeight: 600, fontSize: '14px' }}>בחר עיר מהמפה</span>
            <button
              type="button"
              onClick={() => setShowMap(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {/* Region Tabs */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #e4e6ef',
            background: '#fff'
          }}>
            {Object.entries(REGION_LABELS).map(([region, label]) => (
              <button
                key={region}
                type="button"
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px solid transparent',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '13px',
                  color: '#5e6278'
                }}
                onClick={() => {
                  // Scroll to region
                  const regionEl = document.getElementById(`region-${region}`)
                  if (regionEl) regionEl.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Cities Grid by Region */}
          <div style={{
            maxHeight: '350px',
            overflowY: 'auto',
            padding: '12px'
          }}>
            {Object.entries(CITIES_BY_REGION).map(([region, cities]) => (
              <div key={region} id={`region-${region}`} style={{ marginBottom: '16px' }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#1877f2',
                  marginBottom: '8px',
                  padding: '4px 0',
                  borderBottom: '1px solid #e4e6ef'
                }}>
                  {REGION_LABELS[region as keyof typeof REGION_LABELS]}
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '8px'
                }}>
                  {cities.map((city) => (
                    <button
                      key={city.name}
                      type="button"
                      onClick={() => handleSelectCity(city)}
                      style={{
                        padding: '8px 10px',
                        background: selectedCity?.name === city.name ? '#1877f2' : '#fff',
                        color: selectedCity?.name === city.name ? 'white' : '#181c32',
                        border: '1px solid',
                        borderColor: selectedCity?.name === city.name ? '#1877f2' : '#e4e6ef',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500,
                        textAlign: 'center',
                        transition: 'all 0.2s'
                      }}
                    >
                      {city.nameHe}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default IsraelCitySelector
